/**
 * Authentication Store
 *
 * Client-side only authentication state management
 * Uses AWS Amplify for Cognito integration
 * Stores Access Token (not ID Token) for API calls
 */

import { writable, derived, get } from 'svelte/store';
import { goto } from '$app/navigation';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  signInWithRedirect,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import type { AuthState, User } from '$shared/types/api';
import { configureAmplify } from '../config/amplify-config';
import { getConfig } from '$config';

// Our storage keys
const ID_TOKEN_KEY = 'id_token';
const REFRESH_TOKEN_KEY = 'stat_refresh_token';

// Matches the internal storage prefix used by Amplify v6's DefaultTokenStore
// Source: @aws-amplify/auth/dist/esm/providers/cognito/tokenProvider/constants.mjs
const AMPLIFY_KEY_PREFIX = 'CognitoIdentityServiceProvider';

// Auth state store
const authState = writable<AuthState>({
  user: null,
  idToken: null,
  loading: true,
  error: null,
  isAuthReady: false,
});

// Derived stores for convenience
export const isAuthReady = derived(authState, ($state) => $state.isAuthReady);

export const isAuthenticated = derived(authState, ($state) => $state.isAuthReady && !!$state.user && !!$state.idToken);

export const isAdmin = derived(authState, ($state) => {
  return (
    $state.user?.groups?.includes('Developer') ||
    $state.user?.groups?.includes('admin') ||
    $state.user?.groups?.includes('super_admin') ||
    $state.user?.role === 'admin' ||
    $state.user?.role === 'super_admin' ||
    false
  );
});

/** Cognito Developer group — CLARA rules/settings management (matches API policy). */
export const isDeveloper = derived(authState, ($state) => {
  return $state.user?.groups?.includes('Developer') ?? false;
});

/** STAT internal staff — Cognito Employee group (plus developers/admins). */
export const isStatStaff = derived(authState, ($state) => {
  return (
    $state.user?.groups?.includes('Employee') ||
    $state.user?.groups?.includes('Developer') ||
    $state.user?.groups?.includes('admin') ||
    $state.user?.groups?.includes('super_admin') ||
    $state.user?.role === 'admin' ||
    $state.user?.role === 'super_admin' ||
    false
  );
});

export const currentUser = derived(authState, ($state) => $state.user);

export const authLoading = derived(authState, ($state) => $state.loading);

export const authError = derived(authState, ($state) => $state.error);

// Export the store for direct access if needed
export { authState };

/**
 * Read the refresh token that Amplify stored internally after sign-in.
 * Amplify v6 stores it under:
 *   CognitoIdentityServiceProvider.{clientId}.{username}.refreshToken
 */
function readAmplifyRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  const clientId = getConfig().auth.user_pool_client_id;
  const lastUser = localStorage.getItem(`${AMPLIFY_KEY_PREFIX}.${clientId}.LastAuthUser`);
  if (!lastUser) return null;
  return localStorage.getItem(`${AMPLIFY_KEY_PREFIX}.${clientId}.${lastUser}.refreshToken`);
}

/**
 * Decode the payload of a JWT without verifying the signature.
 * Verification is done server-side; here we only need the claims.
 */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return JSON.parse(atob(padded));
}

/**
 * Exchange a stored refresh token for a new ID token directly via the
 * Cognito UserPools InitiateAuth API. No client secret is required for SPA
 * app clients. Updates auth state and persists the new ID token.
 */
async function signInWithRefreshToken(refreshToken: string): Promise<void> {
  const config = getConfig();
  const region = config.auth.aws_region;
  const clientId = config.auth.user_pool_client_id;

  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      AuthParameters: { REFRESH_TOKEN: refreshToken },
      ClientId: clientId,
    }),
  });

  if (!response.ok) throw new Error('Refresh token exchange failed');

  const data = await response.json();
  const idToken: string | undefined = data?.AuthenticationResult?.IdToken;
  if (!idToken) throw new Error('No ID token in refresh response');

  const payload = decodeJwtPayload(idToken);
  const groups = payload['cognito:groups'] as string[] | undefined;

  const role: User['role'] = groups?.includes('super_admin')
    ? 'super_admin'
    : groups?.includes('Developer') || groups?.includes('admin')
      ? 'admin'
      : 'user';

  const user: User = {
    id: (payload.sub as string) ?? '',
    email: (payload.email as string) ?? '',
    name: (payload.name as string) ?? (payload.given_name as string) ?? '',
    groups: groups ?? [],
    role,
  };

  if (typeof window !== 'undefined') {
    localStorage.setItem(ID_TOKEN_KEY, idToken);
    // Cognito only returns a new refresh token when rotation is enabled on the
    // user pool; fall back to reusing the existing one otherwise.
    const newRefreshToken: string | undefined = data?.AuthenticationResult?.RefreshToken;
    if (newRefreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
    }
  }

  authState.set({ user, idToken, loading: false, error: null, isAuthReady: true });
}

/**
 * Initialize authentication on app mount.
 * 1. Attempts to restore the Amplify session (auto-refreshes via Amplify's
 *    internally stored refresh token if the ID/access tokens have expired).
 * 2. If Amplify has no session, falls back to our explicitly stored refresh
 *    token and silently re-authenticates via the Cognito API.
 */
export async function initAuth(): Promise<void> {
  try {
    configureAmplify();

    // Migration: Remove old access token from localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token');
    }

    const cognitoUser = await getCurrentUser();
    // fetchAuthSession automatically uses the stored refresh token to issue
    // fresh ID/access tokens when the current ones have expired.
    const session = await fetchAuthSession();

    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      throw new Error('No ID token found');
    }

    const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] | undefined;
    const attributes = session.tokens?.idToken?.payload;

    const role: User['role'] = groups?.includes('super_admin')
      ? 'super_admin'
      : groups?.includes('Developer') || groups?.includes('admin')
        ? 'admin'
        : 'user';

    const user: User = {
      id: cognitoUser.userId,
      email: (attributes?.email as string) ?? '',
      name: (attributes?.name as string) ?? (attributes?.given_name as string) ?? '',
      groups: groups ?? [],
      role,
    };

    if (typeof window !== 'undefined') {
      localStorage.setItem(ID_TOKEN_KEY, idToken);
      // Copy the refresh token out of Amplify's internal storage so we can use
      // it to recover a session even if Amplify's storage keys are cleared.
      const refreshToken = readAmplifyRefreshToken();
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }
    }

    authState.set({ user, idToken, loading: false, error: null, isAuthReady: true });
  } catch {
    // Amplify session unavailable — try silent re-auth with our stored refresh token
    if (typeof window !== 'undefined') {
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (storedRefreshToken) {
        try {
          await signInWithRefreshToken(storedRefreshToken);
          return;
        } catch {
          // Refresh token expired or revoked — clear it and require re-login
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
      }
      localStorage.removeItem('auth_user');
      localStorage.removeItem(ID_TOKEN_KEY);
    }

    authState.set({ user: null, idToken: null, loading: false, error: null, isAuthReady: true });
  }
}

/**
 * Register a new Cognito user (email verification required).
 */
export async function signUp(email: string, password: string): Promise<{ needsConfirmation: boolean }> {
  configureAmplify();

  const normalizedEmail = email.trim().toLowerCase();
  const result = await amplifySignUp({
    username: normalizedEmail,
    password,
    options: {
      userAttributes: { email: normalizedEmail },
    },
  });

  return { needsConfirmation: !result.isSignUpComplete };
}

/**
 * Confirm signup with email verification code, then sign in.
 */
export async function confirmSignUpAndSignIn(email: string, code: string, password: string): Promise<void> {
  configureAmplify();

  const normalizedEmail = email.trim().toLowerCase();
  await amplifyConfirmSignUp({
    username: normalizedEmail,
    confirmationCode: code.trim(),
  });

  await signIn(normalizedEmail, password);
}

export async function resendSignUpCode(email: string): Promise<void> {
  configureAmplify();
  await amplifyResendSignUpCode({ username: email.trim().toLowerCase() });
}

/**
 * Start a password reset. Cognito emails a 6-digit confirmation code — the
 * pool uses code-based verification, not a magic link — which the caller
 * collects on the /reset-password screen. Mirrors the mobile app's flow.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  configureAmplify();
  await amplifyResetPassword({ username: email.trim().toLowerCase() });
}

/**
 * Complete a password reset with the emailed code and the new password.
 * The user is not signed in afterwards; they return to /login.
 */
export async function confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
  configureAmplify();
  await amplifyConfirmResetPassword({
    username: email.trim().toLowerCase(),
    confirmationCode: code.trim(),
    newPassword,
  });
}

/**
 * Sign in with email and password (SRP flow)
 */
export async function signIn(email: string, password: string): Promise<void> {
  configureAmplify();

  const { isSignedIn } = await amplifySignIn({
    username: email,
    password,
  });

  if (!isSignedIn) {
    throw new Error('Sign in failed');
  }

  // Fetch user data after successful sign in
  await initAuth();
}

/**
 * Sign in by answering a Cognito CUSTOM_AUTH challenge with a QR-login nonce.
 * The nonce must already be `approved` server-side (mobile scan completed).
 */
export async function signInWithQr(email: string, nonce: string): Promise<void> {
  configureAmplify();

  const start = await amplifySignIn({
    username: email,
    options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
  });

  if (start.nextStep.signInStep !== 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
    throw new Error(`Unexpected sign-in step: ${start.nextStep.signInStep}`);
  }

  const confirm = await confirmSignIn({ challengeResponse: nonce });
  if (!confirm.isSignedIn) {
    throw new Error('QR challenge rejected');
  }

  await initAuth();
}

export type SocialProvider = 'Google' | 'Apple' | 'Microsoft';

// sessionStorage keys used to survive the round-trip through the Cognito
// hosted UI: which provider started the flow, and whether we already used
// our single silent retry after the PreSignUp trigger linked the account.
const SOCIAL_PROVIDER_KEY = 'social_login_provider';
const SOCIAL_RETRY_KEY = 'social_login_retried';

/**
 * The very first federated sign-in for an email intentionally fails: the
 * PreSignUp Lambda links the identity to the native user and aborts the
 * shadow-user creation. Cognito surfaces either its own "Already found an
 * entry for username" or our Lambda's LINKED_RETRY marker; both mean
 * "retry once and it will succeed".
 */
function isLinkRetryError(message: string): boolean {
  return message.includes('Already found an entry for username') || message.includes('LINKED_RETRY');
}

/**
 * Start a social sign-in by redirecting to the identity provider via the
 * Cognito hosted UI. The browser navigates away; the flow completes on
 * /auth/callback via handleSocialRedirect().
 */
export async function signInWithProvider(provider: SocialProvider): Promise<void> {
  configureAmplify();
  sessionStorage.setItem(SOCIAL_PROVIDER_KEY, provider);
  await signInWithRedirect({
    provider: provider === 'Microsoft' ? { custom: 'Microsoft' } : provider,
  });
}

/**
 * Complete a social sign-in on the /auth/callback page.
 *
 * Amplify exchanges the authorization code automatically once configured;
 * this waits for that to finish (Hub event or session polling), hydrates
 * the auth store, and performs the one silent retry after account linking.
 *
 * Returns 'retrying' when a redirect back to the provider has been started —
 * the caller should keep showing its spinner and do nothing else.
 */
export async function handleSocialRedirect(errorDescription: string | null): Promise<'signed-in' | 'retrying'> {
  configureAmplify();
  const provider = sessionStorage.getItem(SOCIAL_PROVIDER_KEY) as SocialProvider | null;

  if (errorDescription) {
    if (isLinkRetryError(errorDescription) && provider && !sessionStorage.getItem(SOCIAL_RETRY_KEY)) {
      sessionStorage.setItem(SOCIAL_RETRY_KEY, 'true');
      // The IdP session cookie makes the second attempt promptless.
      await signInWithRedirect({
        provider: provider === 'Microsoft' ? { custom: 'Microsoft' } : provider,
      });
      return 'retrying';
    }
    sessionStorage.removeItem(SOCIAL_PROVIDER_KEY);
    sessionStorage.removeItem(SOCIAL_RETRY_KEY);
    throw new Error(errorDescription);
  }

  await waitForRedirectSignIn();
  sessionStorage.removeItem(SOCIAL_PROVIDER_KEY);
  sessionStorage.removeItem(SOCIAL_RETRY_KEY);
  await initAuth();

  if (!get(authState).user) {
    throw new Error('Social sign-in did not establish a session');
  }
  return 'signed-in';
}

/**
 * Wait until Amplify has finished processing the OAuth redirect. The Hub
 * event can fire before this runs (Amplify starts the token exchange when
 * it is configured), so a session check runs alongside the listener.
 */
async function waitForRedirectSignIn(timeoutMs = 15000): Promise<void> {
  const hasSession = async () => {
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  };

  if (await hasSession()) return;

  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const settle = (error?: Error) => {
      if (settled) return;
      settled = true;
      unsubscribe();
      clearInterval(pollTimer);
      clearTimeout(timeoutTimer);
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    };

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        settle();
      } else if (payload.event === 'signInWithRedirect_failure') {
        const message = (payload.data as { error?: Error } | undefined)?.error?.message ?? 'Social sign-in failed';
        settle(new Error(message));
      }
    });
    const pollTimer = setInterval(async () => {
      if (await hasSession()) settle();
    }, 500);
    const timeoutTimer = setTimeout(() => settle(new Error('Social sign-in timed out')), timeoutMs);
  });
}

/**
 * Whether the signed-in user authenticated through a linked social provider.
 * Linked users still hold a (random) native password, so password reset stays
 * available as an escape hatch — use this only to soften password-centric UI.
 */
export function isFederatedUser(): boolean {
  const token = get(authState).idToken;
  if (!token) return false;
  try {
    const identities = decodeJwtPayload(token).identities;
    return Array.isArray(identities) && identities.length > 0;
  } catch {
    return false;
  }
}

/**
 * Sign out and clear all auth state
 * @param global - If true, signs out from all devices (invalidates all sessions)
 *                 If false, signs out only from current device
 */
export async function signOut(global = false): Promise<void> {
  try {
    await amplifySignOut({ global });
  } catch (error) {
    console.error('Sign out error:', error);
  } finally {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_token');
      localStorage.removeItem(ID_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem('sidebar-collapsed');
    }

    authState.set({
      user: null,
      idToken: null,
      loading: false,
      error: null,
      isAuthReady: true,
    });
  }
}

/**
 * Clear auth error
 */
export function clearAuthError(): void {
  authState.update((state) => ({ ...state, error: null }));
}

// Refresh a minute early, so a token can't lapse between being read and reaching the API.
const TOKEN_REFRESH_MARGIN_MS = 60_000;
let refreshInFlight: Promise<string | null> | null = null;

function expiresSoon(token: string): boolean {
  try {
    return Number(decodeJwtPayload(token).exp) * 1000 - Date.now() < TOKEN_REFRESH_MARGIN_MS;
  } catch {
    return true;
  }
}

/**
 * The signed-in user's ID token for an API call, refreshed first when it is about to expire.
 * Null when signed out, or when the session has ended (the caller is then sent to /login).
 */
export async function getIdToken(): Promise<string | null> {
  const token = get(authState).idToken;
  if (!token || !expiresSoon(token)) return token;
  refreshInFlight ??= refreshIdToken(token).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function refreshIdToken(current: string): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      localStorage.setItem(ID_TOKEN_KEY, idToken);
      authState.update((state) => ({ ...state, idToken }));
      return idToken;
    }
  } catch {
    // Offline or Cognito unavailable: this request fails on its own and the next one tries again.
    return current;
  }

  // Amplify has no session (it rejected the refresh token, or its storage was cleared), so try our own copy.
  const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (storedRefreshToken) {
    try {
      await signInWithRefreshToken(storedRefreshToken);
      return get(authState).idToken;
    } catch (error) {
      if (error instanceof TypeError) return current;
    }
  }

  await expireSession();
  return null;
}

/** Signs out and opens /login, saying the session ended. */
export async function expireSession(): Promise<void> {
  await signOut();
  void goto('/login?reason=session_expired');
}

/**
 * Check if user has specific role
 */
export function hasRole(role: 'admin' | 'super_admin' | 'user'): boolean {
  return get(authState).user?.role === role;
}

/**
 * Check if user is in specific group
 */
export function inGroup(groupName: string): boolean {
  return get(authState).user?.groups?.includes(groupName) ?? false;
}
